import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Flex } from 'shared';

import { useChatContext } from 'features/chat/context/chat';

import { UnreadMessagesAnchor } from './components/UnreadMessagesAnchor';
import { BaseMessage } from './config/types/Message';
import { useLatestMessageShouldTriggerScroll } from './hooks/useLatestMessageShouldTriggerScroll';
import { usePreserveScrollTopWhenHistoryMessagesLoaded } from './hooks/usePreserveScrollTopWhenHistoryMessagesLoaded';

type ScrollContainerProps = {
  messages: BaseMessage[];
  children: ReactNode;
  onScrollToBottom: () => void;
};

// This component contains all the logic for auto scrolling of chat
// When chat is first open, it should appear scrolled to bottom
// If user reads the history and new message appears it should stay and 'Show new messages' button appears
// If the chat is scrolled up to history messages and user sends his own message, the chat should be scrolled down
// If users own scheduled message is sent to the chat, the chat should not be scrolled down
// If user scrolls the chat manually, instead of pressing the 'Show new messages' button -> upon reaching the bottom
// the 'Show new messages' button should be hidden automatically

const SCROLL_BOTTOM_THRESHOLD = 2;

export const ScrollContainer = ({
  messages,
  onScrollToBottom,
  children,
}: ScrollContainerProps) => {
  // visible chat area
  const outerRef = useRef<HTMLDivElement>(null);
  // visible + invisible messages container
  const innerRef = useRef<HTMLDivElement>(null);

  const prevLastMessage = useRef<BaseMessage | null>();

  // used for showing or hiding the 'Show new messages' button
  const [isScrollToBottomButtonVisible, setIsScrollToBottomButtonVisible] =
    useState(false);

  const { unreadMessagesCount } = useChatContext();

  const shouldTriggerScrollToBottom =
    useLatestMessageShouldTriggerScroll(messages);

  usePreserveScrollTopWhenHistoryMessagesLoaded({
    outerRef,
    innerRef,
    messages,
  });

  const shouldScrollToBottomOnNewMessage = useCallback(() => {
    const isAtBottom =
      (outerRef.current?.scrollTop ?? 0) <= SCROLL_BOTTOM_THRESHOLD;
    const hasUnreadMessages = unreadMessagesCount > 0;

    if (hasUnreadMessages && !isAtBottom) {
      return false;
    }

    return isAtBottom || shouldTriggerScrollToBottom;
  }, [outerRef, shouldTriggerScrollToBottom, unreadMessagesCount]);

  useEffect(() => {
    const isNewMessageSent =
      prevLastMessage.current !== undefined &&
      prevLastMessage.current?.messageId !==
        messages[messages.length - 1].messageId;

    if (isNewMessageSent && shouldScrollToBottomOnNewMessage()) {
      handleScrollToBottom();
    } else {
      setIsScrollToBottomButtonVisible(true);
    }

    // save prev value for future calculations
    prevLastMessage.current = messages[messages.length - 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const isScrolledToBottom = useCallback(() => {
    return (outerRef.current?.scrollTop ?? 0) <= SCROLL_BOTTOM_THRESHOLD;
  }, [outerRef]);

  const handleScroll = useCallback(() => {
    // if user scrolled to bottom manually we hide the 'Show new messages' button
    // and call 'read all messages' mutation

    if (isScrolledToBottom() && isScrollToBottomButtonVisible) {
      setIsScrollToBottomButtonVisible(false);
    }
  }, [isScrolledToBottom, isScrollToBottomButtonVisible]);

  // the scroll handler is not throttled cause in that case it misses some events

  useEffect(() => {
    const outerRefCurrent = outerRef.current;
    outerRefCurrent?.addEventListener('scroll', handleScroll);

    return () => {
      outerRefCurrent?.removeEventListener('scroll', handleScroll);
    };
  }, [outerRef, handleScroll]);

  const handleScrollToBottom = useCallback(() => {
    outerRef?.current?.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    setIsScrollToBottomButtonVisible(false);
    onScrollToBottom();
  }, [onScrollToBottom]);

  return (
    <>
      <Flex
        ref={outerRef}
        position="relative"
        height="100%"
        overflowY="auto"
        flexDirection="column-reverse"
      >
        <Flex
          ref={innerRef}
          position="relative"
          p="0 1rem 1rem"
          flexDirection="column"
        >
          {children}
        </Flex>
      </Flex>
      {isScrollToBottomButtonVisible && (
        <UnreadMessagesAnchor onClick={handleScrollToBottom} />
      )}
    </>
  );
};
